import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { AuthShell } from "@/components/customer/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ensureCustomerProfile } from "@/lib/customer/customer-api";
import { EmailVerificationNotice } from "@/components/customer/EmailVerificationNotice";
import { hasEmailLinkError, isEmailUnverified } from "@/lib/customer/email-verification";

export const Route = createFileRoute("/account/sign-in")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Sign in to your account — Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content: "Sign in to manage your profile and saved delivery addresses.",
      },
      { property: "og:title", content: "Sign in — Poonam Kapur Healthy Kitchen" },
      {
        property: "og:description",
        content: "Sign in to manage your profile and saved delivery addresses.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SignInPage,
});

function SignInPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch() as { redirect?: string };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const inFlight = useRef(false);

  async function handleSocialLogin(provider: "google" | "apple") {
    setBusy(true);
    setError(null);
    try {
      if (redirect) sessionStorage.setItem("post_auth_redirect", redirect);
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin + "/account/sign-in",
      });
      if (result.error) throw result.error;
      if (result.redirected) return;
      await ensureCustomerProfile().catch(() => null);
      const dest = sessionStorage.getItem("post_auth_redirect");
      sessionStorage.removeItem("post_auth_redirect");
      if (dest && dest.startsWith("/") && !dest.startsWith("//")) window.location.href = dest;
      else navigate({ to: "/account", replace: true });
    } catch (err: any) {
      setError(err.message || `Failed to sign in with ${provider}`);
      setBusy(false);
    }
  }

  // After an OAuth or magic-link return, the session arrives on this public page; then continue.
  useEffect(() => {
    if (hasEmailLinkError(window.location.search, window.location.hash)) {
      setError("This email link is invalid, expired, or already used. Try signing in if you verified already. Otherwise, sign in with your email and password to request a new verification email.");
      return;
    }
    let done = false;
    const go = async () => {
      const { data } = await supabase.auth.getUser();
      if (done || !data.user) return;
      done = true;
      await ensureCustomerProfile().catch(() => null);
      const dest = sessionStorage.getItem("post_auth_redirect") || redirect;
      sessionStorage.removeItem("post_auth_redirect");
      if (dest && dest.startsWith("/") && !dest.startsWith("//")) window.location.href = dest;
      else navigate({ to: "/account", replace: true });
    };
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") go();
    });
    go();
    return () => {
      done = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);

    try {
      if (redirect) sessionStorage.setItem("post_auth_redirect", redirect);
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: window.location.origin + "/account/sign-in",
        },
      });

      if (otpError) throw otpError;
      setNotice("Check your email for a sign-in link.");
    } catch (err: any) {
      setError(err.message || "Failed to send magic link.");
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
    const { error: signInError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (signInError) {
      if (isEmailUnverified(signInError)) {
        setVerificationEmail(email.trim());
        setPassword("");
      } else setError(signInError.message);
      return;
    }
    // Profile row is created on first successful sign-in.
    await ensureCustomerProfile().catch(() => null);
    
    if (redirect) {
      window.location.href = redirect; // Hard redirect to ensure search params etc are handled by router correctly
    } else {
      navigate({ to: "/account", replace: true });
    }
    } catch {
      setError("We couldn’t sign you in. Please check your connection and try again.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Welcome back"
      description="Sign in to your Healthy Kitchen account"
      footer={
        <div className="text-center">
          <Link to="/account/sign-up" className="text-sm text-muted-foreground hover:text-foreground">
            Create account
          </Link>
        </div>
      }
    >
      {verificationEmail ? <EmailVerificationNotice email={verificationEmail} redirect={redirect}
        onBack={() => { setVerificationEmail(null); setError(null); setNotice(null); }} /> :
      <div className="space-y-6">
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="signin-email" className="text-xs font-bold uppercase tracking-wider text-foreground/70">
              Email
            </Label>
            <Input
              id="signin-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 border-border/60 bg-background/50"
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="signin-password" className="text-xs font-bold uppercase tracking-wider text-foreground/70">
                Password
              </Label>
            </div>
            <div className="relative">
              <Input
                id="signin-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 border-border/60 bg-background/50 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88L4.62 4.62"/><path d="M8.47 16.39L2 22"/><path d="M2 2l20 20"/><path d="M9.51 9.51L8.59 8.59"/><path d="M12.73 17.27l-.73.73"/><path d="M16.41 16.41L17.27 17.27"/><path d="M21.16 7.84C21.69 8.57 22 9.48 22 12c0 2.5-2.5 4.5-5 4.5-1.05 0-2.02-.35-2.82-.94"/><path d="M6.59 6.59C6.07 7.21 5.48 7.9 5 8.5c-1.5 2-2 3.5-2 3.5 0 0 5 8 11 8 1.41 0 2.7-.44 3.82-1.22"/><path d="M10.59 10.59c-.38.38-.59.89-.59 1.41 0 1.1.9 2 2 2 .52 0 1.03-.21 1.41-.59"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            <div className="flex justify-end">
              <Link
                to="/account/forgot-password"
                className="text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {notice && <p className="text-sm text-primary font-medium">{notice}</p>}

          <Button type="submit" className="h-12 w-full font-bold uppercase tracking-widest bg-primary text-primary-foreground" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border/60"></span>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground tracking-widest font-bold">or</span>
          </div>
        </div>

        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            className="h-12 w-full font-bold uppercase tracking-widest border-border/60 hover:bg-secondary/50 text-foreground"
            onClick={handleMagicLink}
            disabled={busy}
          >
            Continue with Magic Link
          </Button>

          <Button
            type="button"
            variant="outline"
            className="h-12 w-full font-bold uppercase tracking-widest border-border/60 hover:bg-secondary/50 flex items-center justify-center gap-3 text-foreground"
            onClick={() => handleSocialLogin("google")}
            disabled={busy}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/><path d="M1 1h22v22H1z" fill="none"/></svg>
            Continue with Google
          </Button>

          <Button
            type="button"
            variant="outline"
            className="h-12 w-full font-bold uppercase tracking-widest border-border/60 hover:bg-secondary/50 flex items-center justify-center gap-3 text-foreground"
            onClick={() => handleSocialLogin("apple")}
            disabled={busy}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg"><path d="M17.05 20.28c-.96.95-2.21 1.72-3.72 1.72-1.47 0-2.11-.91-3.66-.91-1.55 0-2.26.91-3.66.91-1.51 0-2.76-.77-3.72-1.72-2.31-2.31-2.31-6.1 0-8.41.96-.95 2.21-1.72 3.72-1.72 1.47 0 2.11.91 3.66.91 1.55 0 2.26-.91 3.66-.91 1.51 0 2.76.77 3.72 1.72 1.15 1.16 1.72 2.77 1.72 4.2 0 1.44-.57 3.05-1.72 4.21zM12 7.12c-.18-2.48 1.88-4.59 4.38-4.77.18 2.48-1.88 4.59-4.38 4.77z" fill="currentColor"/></svg>
            Continue with Apple
          </Button>
        </div>
      </div>}
    </AuthShell>
  );
}
